import { ChangeDetectorRef, Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";

import { BaseComponent, ProductService, UserService, environment } from "@mypxplat/xplat/core";
import { AppService, DiscordGiftBenefit, WebCommunityService } from "@mypxplat/xplat/web/core";
import { RegisterProductComponent } from "../register-product/register-product.component";

/** Which of the states the member is looking at. One property, so the template branches on a value. */
type ClaimState = "loading" | "error" | "not_signed_in" | "not_linked" | "needs_tmp" | "claim" | "claimed" | "offer_closed" | "unsupported_subscription";

/** Readable biller names for the "we cannot extend this" copy. Anything else is shown verbatim. */
const PROVIDER_LABELS: Record<string, string> = {
  xchange: "Xchange",
  recharge: "Recharge",
  bluesnap: "BlueSnap",
  stripe: "Stripe",
  presonus: "PreSonus",
};

/**
 * What a Tone Master Pro member gets for moving over, and what they still have to do to get it.
 *
 * Shown after the Discord OAuth round trip (`?discordLinkSuccess=true`) in place of the welcome
 * modal, which by that point is asking them to do something they have just done.
 *
 * The entitlement has two requirements — a linked Discord account and a registered Tone Master
 * Pro — and a member can arrive having met either, neither or both. Rather than infer that from
 * several flags, this renders a checklist of both and a single action for whatever is missing.
 *
 * Everything is decided by the backend's gift state (`GET /oauth/discord/link`), which returns
 * the same object `POST /oauth/discord/redeem` returns, so a claim updates the view without a
 * second round trip. See `buildGiftState` in paeapi's discordController.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-discord-claim",
  templateUrl: "discord-claim.component.html",
  styleUrls: ["discord-claim.component.scss"],
  standalone: false,
})
export class DiscordClaimComponent extends BaseComponent implements OnInit {
  public state: ClaimState = "loading";
  /**
   * Where the Discord round trip should come back to, handed to the embedded link panel in the
   * `not_linked` edge case. Set by whoever opens this modal.
   */
  public returnTo = "";
  /**
   * Show the migration explainer above the checklist. Set when this is a cold entry — the
   * "Here from Discord?" link — rather than the tail of a flow the member is already in.
   */
  @Input() showMigrationIntro = false;
  public working = false;
  public errorMessage = "";

  /**
   * Set while the register-product modal is open over this one, so the reload that follows can
   * tell "they just registered their Tone Master Pro" from "they already had one" — only the
   * first is worth celebrating. Cleared as soon as it fires.
   */
  private _awaitingTmpRegistration = false;

  /** Checklist rows. Rendered from properties, never from getters — see the template. */
  public linked = false;
  public requiresToneMasterPro = false;
  public hasToneMasterPro = true;
  public showTmpRequirement = false;
  /** Whether there is still something to claim, and therefore a reason to explain the offer. */
  public showOfferExplainer = false;

  public ownsGiftProduct = false;

  /**
   * What the backend says this member would receive, and who bills them.
   *
   * The one field to branch pre-claim copy on. Three outcomes are possible — a free license, a
   * year of updates on the license they already own, or a year of the subscription they already
   * pay for — and promising the wrong one is worse than promising nothing, which is what `null`
   * (the backend could not work it out) falls back to.
   */
  public giftBenefit: DiscordGiftBenefit = null;
  public benefitProvider: string | null = null;
  /** Human name for `benefitProvider`, for the unsupported-biller copy. */
  public providerLabel = "";

  // Pre-computed rather than compared in the template: bindings re-evaluate every change
  // detection pass, and the rule here is no method calls and no getters in templates.
  public benefitIsLicense = false;
  public benefitIsExtension = false;
  public benefitIsSubscription = false;
  /** Stripe coupons start at the next renewal; a PreSonus row moves its end date immediately. */
  public benefitStartsAtRenewal = false;
  /** No benefit could be resolved — generic copy, promise nothing specific. */
  public benefitUnknown = false;

  /**
   * What the member actually received, read from `giftRedemption.status` — NOT from
   * `giftBenefit`, which after a claim describes a hypothetical, and not from `ownsGiftProduct`,
   * which is true for everyone once a license lands and would tell a first-time claimant that
   * time was added to a license they never had.
   *
   * Andy's note says the redeemed/extended split exists only for reporting and the copy can be
   * the same. That holds for the account page's one-line banner; this modal explains the offer,
   * so it says which happened. The subscription case has to differ regardless — nobody who just
   * got 12 months of their subscription should be told to download anything.
   */
  public claimWasExtension = false;
  public claimWasSubscription = false;
  /**
   * Read from the redemption RECORD's provider rather than the live benefit: what they were
   * told at claim time is what the record says happened, and the live benefit is recomputed
   * from a subscription that could have changed since.
   */
  public claimStartsAtRenewal = false;

  constructor(
    userService: UserService,
    public appService: AppService,
    public activeModal: NgbActiveModal,
    private _community: WebCommunityService,
    private _productService: ProductService,
    private _router: Router,
    private _cdr: ChangeDetectorRef
  ) {
    super(userService);
  }

  ngOnInit() {
    this.load();
  }

  load() {
    // Signed out: the status endpoint would only 401, and what they need is a way in, not an
    // error. Saves a round trip on every cold click too.
    if (!this.user) {
      this.state = "not_signed_in";
      this._cdr.markForCheck();
      return;
    }
    this.state = "loading";
    this.errorMessage = "";
    this._community.getDiscordLink().subscribe({
      next: (res: any) => this._apply(res),
      error: () => {
        this.state = "error";
        this._cdr.markForCheck();
      },
    });
  }

  /** Adopts the backend's gift state verbatim — both endpoints return the same shape. */
  private _apply(res: any) {
    this.linked = !!res?.linked;
    this.requiresToneMasterPro = !!res?.requiresToneMasterPro;
    // Default true so a backend not yet sending the field never blocks anyone.
    const hadToneMasterPro = this.hasToneMasterPro;
    this.hasToneMasterPro = res?.hasToneMasterPro !== false;
    // They went and registered the pedal while this modal waited. Worth a moment before the
    // checklist quietly ticks over to the claim step.
    if (this._awaitingTmpRegistration && this.hasToneMasterPro && !hadToneMasterPro) {
      this._awaitingTmpRegistration = false;
      this.appService.showConfetti();
    }
    this.ownsGiftProduct = !!res?.ownsGiftProduct;
    this.showTmpRequirement = this.requiresToneMasterPro;
    this._applyBenefit(res?.giftBenefit ?? null, res?.giftBenefitProvider ?? null);

    const claimed = !!res?.giftRedemption?.redeemedAt || !!res?.giftRedeemed;
    const status = res?.giftRedemption?.status;
    this.claimWasExtension = status === "extended";
    this.claimWasSubscription = status === "subscription-extended";
    this.claimStartsAtRenewal = this.claimWasSubscription && res?.giftRedemption?.provider === "stripe";
    const offerOpen = res?.giftOpen !== false;

    // Claimed wins over everything: it is the end state, and a closed offer afterwards is
    // irrelevant to someone who already has the software.
    if (claimed) this.state = "claimed";
    else if (!this.linked) this.state = "not_linked";
    else if (!offerOpen) this.state = "offer_closed";
    // Hardware before biller, matching the backend: `redeemGift` runs the Tone Master Pro gate
    // ahead of resolving the benefit, so a subscriber without a TMP is told about the TMP first.
    else if (this.requiresToneMasterPro && !this.hasToneMasterPro) this.state = "needs_tmp";
    else if (this.giftBenefit === "unsupported") this.state = "unsupported_subscription";
    else this.state = "claim";
    this._applyExplainerVisibility();
    this._cdr.markForCheck();
  }

  private _applyBenefit(benefit: DiscordGiftBenefit, provider: string | null) {
    this.giftBenefit = benefit;
    this.benefitProvider = provider;
    this.benefitIsLicense = benefit === "license";
    this.benefitIsExtension = benefit === "license_extension";
    this.benefitIsSubscription = benefit === "subscription_extension";
    this.benefitUnknown = benefit === null;
    // Only Stripe defers: the coupon lands on the next invoice, so nothing visibly changes on
    // the day they claim. A PreSonus row has its end date moved there and then.
    this.benefitStartsAtRenewal = benefit === "subscription_extension" && provider === "stripe";
    this.providerLabel = provider ? (PROVIDER_LABELS[provider] ?? provider) : "";
  }

  /**
   * The explainer justifies the checklist — what the two requirements are FOR — so it belongs
   * only while one of them is still outstanding.
   *
   * Deliberately NOT shown in the `claim` state. Both ticks are green by then, which makes "two
   * things unlock it" the wrong tense, and the paragraph above the button already says what they
   * are about to get. Showing both said the same thing twice.
   *
   * Unknown or unsupported benefits hide it too: it cannot name an offer it could not resolve.
   */
  private _applyExplainerVisibility() {
    const requirementOutstanding = this.state === "not_linked" || this.state === "needs_tmp";
    this.showOfferExplainer = requirementOutstanding && !this.benefitUnknown && this.giftBenefit !== "unsupported";
  }

  /**
   * Claims in place rather than sending them to the account page. The backend returns the full
   * post-claim state, so the modal moves straight to its "claimed" view.
   */
  claim() {
    if (this.working) return;
    this.working = true;
    this.errorMessage = "";
    this._community.redeemDiscordGift().subscribe({
      next: (res: any) => {
        this.working = false;
        // `alreadyRedeemed` is success, not an error — they claimed on another surface.
        this._apply(res);
        // Only for a claim that happened HERE. Someone whose claim landed on another surface
        // gets the same end state, but confetti for a thing they already had reads as a glitch.
        if (!res?.alreadyRedeemed && this.state === "claimed") this.appService.showConfetti();
        // The license exists server-side now, but the cached product list does not know. The
        // product detail page resolves from `listProductDataMap`, so without this the member
        // lands on a page that cannot find what they were just given — and their product list
        // stays wrong until a manual reload.
        this._productService.getProducts(true).subscribe({ error: () => {} });
      },
      error: (err: any) => {
        this.working = false;
        const reason = err?.error?.reason;
        if (reason === "needs_tone_master_pro") {
          // Re-render as the register step rather than showing an error: nothing has gone
          // wrong, they simply have one requirement left. The claim is NOT consumed in this
          // case — the backend checks the hardware before writing it.
          this.requiresToneMasterPro = true;
          this.hasToneMasterPro = false;
          this.showTmpRequirement = true;
          this.state = "needs_tmp";
        } else if (reason === "offer_ended") {
          this.state = "offer_closed";
        } else if (reason === "unsupported_subscription_provider") {
          // Not a failure and not retryable: the claim is deliberately left unconsumed so they
          // can come back once support has moved them, or once the subscription lapses.
          this._applyBenefit("unsupported", err?.error?.provider ?? this.benefitProvider);
          this.state = "unsupported_subscription";
          this._applyExplainerVisibility();
        } else {
          this.errorMessage = this.translateKeyFor(reason);
        }
        this._cdr.markForCheck();
      },
    });
  }

  /** Errors that leave the member on the same step, as a translation key. */
  private translateKeyFor(reason: string): string {
    if (reason === "not_linked") return "discord.gift_not_linked";
    if (reason === "not_verified") return "discord.gift_not_verified";
    return "discord.gift_error_generic";
  }

  /**
   * Opens the existing register-product modal over this one and re-reads state on close, so the
   * checklist ticks over without a reload. Same pattern `create-post-input` uses to let an
   * ownership gate re-evaluate.
   */
  registerToneMasterPro() {
    this._awaitingTmpRegistration = true;
    const ref = this.appService.showModal(RegisterProductComponent, {
      size: "lg",
      ariaLabelledBy: "modal-title",
      container: this.appService.appContainer,
    });
    const done = () => this.load();
    ref?.result?.then(done, done);
  }

  /**
   * Leaves for the hosted login, returning to wherever this was opened from — `returnTo`
   * carries the query string, so whichever marker opened this modal survives and it reopens.
   */
  signIn() {
    this.appService.redirectToConnect(true, false);
  }

  /**
   * Where a subscription extension ends: their plan, not the Fender Studio Pro product page.
   * They received months, not software, and have nothing new to download.
   */
  viewSubscription() {
    this._router.navigate(["/account/plan"]).then(() => this.activeModal.close());
  }

  /** The one place this modal navigates: the product they now own. */
  viewProduct() {
    // Navigate BEFORE closing. Closing fires the opener's query-param cleanup, which is itself
    // a navigation — doing it first put two in flight at once and the member could land back
    // where they started instead of on the product.
    this._router.navigate(["/products/detail/", environment.product_ids.fsp8pro]).then(() => this.activeModal.close());
  }

  close() {
    this.activeModal.close();
  }
}
